"use client";
import { PageOptions } from "@/features/dashboard/DashboardStateContext";
import {
  useAuthenticated,
  useHasStations,
  useOrgFeatureFlag
} from "@gettactic/hooks";
import { isVisitorManagementEnabled } from "@gettactic/helpers/src/featureFlags";
import { TabsList, TabsTrigger } from "@cmp-src/primitives/tabs";
import { cn } from "@gettactic/helpers";
import { ArrowLeftIcon } from "lucide-react";
import React, { useMemo } from "react";
export interface IDashboardTabsListProps {
  selectedEvent: string;
  setSelectedEvent: (event: string | null) => void;
  hasPages: number | boolean | undefined;
  showAttendanceTab: boolean;
  setPage: (page: PageOptions) => void;
  defaultPage: PageOptions;
  isMobile?: boolean;
}

export const DashboardTabsList: React.FC<IDashboardTabsListProps> = ({
  selectedEvent,
  setSelectedEvent,
  setPage,
  hasPages,
  showAttendanceTab,
  defaultPage,
  isMobile = false
}) => {
  const { hasStations } = useHasStations();
  const visitorManagementFlag = useOrgFeatureFlag("visitor_management");
  const isVisitorMgmtEnabled = isVisitorManagementEnabled(
    visitorManagementFlag
  );
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  // Bridgestone launch customization (sc-9072): the dashboard defaults to
  // the Map tab for this org (see getDefaultDashboardTab), so the Schedules
  // tab is hidden entirely rather than just deprioritized — except while
  // viewing an event, where this same trigger doubles as the "Back" button.
  const isBridgestoneOrg = authenticatedUser?.slug === "bridgestone";

  const positions = useMemo(() => {
    // we show alternative order for users that have privacy enabled, or for
    // Bridgestone (Schedules hidden entirely — see isBridgestoneOrg above)
    const alternativeOrder =
      (!showAttendanceTab || isBridgestoneOrg) && !selectedEvent && !isMobile;
    return {
      liveMap: 0,
      siteInfo: alternativeOrder ? 1 : 4,
      attendance: alternativeOrder ? 2 : 1,
      visitors: alternativeOrder ? 3 : 2,
      news: alternativeOrder ? 4 : 3
    };
  }, [showAttendanceTab, selectedEvent, isMobile, isBridgestoneOrg]);

  return (
    <TabsList
      className={cn(
        "w-full justify-center md:mt-3 mb-0",
        "items-center md:items-start",
        "justify-start"
      )}
      showTabsAsDropdownInMobile
    >
      {!selectedEvent && (
        <TabsTrigger
          variant="minimalistic"
          value="liveMap"
          className={`order-${positions.liveMap}`}
        >
          Map
        </TabsTrigger>
      )}
      {Boolean(
        selectedEvent || ((showAttendanceTab || isMobile) && !isBridgestoneOrg)
      ) && (
        <TabsTrigger
          variant={selectedEvent ? "default" : "minimalistic"}
          className={cn(
            selectedEvent ? " mb-2.5 pr-0" : isMobile ? "pb-0" : "pb-2",
            `order-${positions.attendance}`
          )}
          value="schedules"
          onClick={
            selectedEvent
              ? () => {
                  setSelectedEvent(null);

                  // will be Attendance or SiteInfo (if privacy is enabled)
                  setPage(defaultPage);
                }
              : undefined
          }
        >
          {selectedEvent ? (
            <span
              className="flex items-center gap-2 font-bold"
              style={{ fontSize: "13px" }}
            >
              <ArrowLeftIcon className="size-4" />
              Back{" "}
            </span>
          ) : (
            "Schedules"
          )}
        </TabsTrigger>
      )}
      {Boolean(!selectedEvent && hasStations && isVisitorMgmtEnabled) && (
        <TabsTrigger
          variant="minimalistic"
          value="visitors"
          className={`order-${positions.visitors}`}
        >
          Visitors
        </TabsTrigger>
      )}
      {!selectedEvent && (
        <TabsTrigger
          variant="minimalistic"
          value="news"
          className={`order-${positions.news}`}
        >
          News
        </TabsTrigger>
      )}
      {Boolean(!selectedEvent && hasPages) && (
        <TabsTrigger
          variant="minimalistic"
          value="siteInfo"
          className={`order-${positions.siteInfo}`}
        >
          Site Info
        </TabsTrigger>
      )}
    </TabsList>
  );
};
